/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

const DEFAULT_CLIENT_ID = '7d5f2434-4053-4c90-b8ea-dae1d258187a';

const viteEnv =
    typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : {};

function readEnv(key, fallback = '') {
    const nodeEnv =
        typeof process !== 'undefined' && process.env ? process.env : {};
    return viteEnv[key] ?? nodeEnv[key] ?? fallback;
}

const tenantId        = import.meta.env.VITE_ENTRA_TENANT_ID        || '';
const tenantSubdomain = import.meta.env.VITE_ENTRA_TENANT_SUBDOMAIN || '';
const clientId        = import.meta.env.VITE_ENTRA_CLIENT_ID        || DEFAULT_CLIENT_ID;
const authMode        = import.meta.env.VITE_ENTRA_AUTH_MODE        || 'ciam';
const authorityOverride = readEnv('VITE_ENTRA_AUTHORITY');

/** OIDC scopes required for Bad Dog auth handoff (ID token). */
export const entraLoginScopes = ['openid', 'profile', 'email'];

const TENANT_GUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTenantGuid(value) {
    return TENANT_GUID_REGEX.test(String(value || ''));
}

/**
 * Build MSAL authority URL. Must match Bad Dog TokenValidator (ENTRA_AUTH_MODE).
 * BioMADEEntra is Entra External ID (CIAM): tokens use *.ciamlogin.com issuer, not login.microsoftonline.com.
 * @param {string} entraTenantId - Azure tenant GUID (VITE_ENTRA_TENANT_ID).
 * @param {'workforce'|'ciam'} [mode='ciam']
 * @param {string} [subdomain] - CIAM tenant subdomain, e.g. BioMADEEntra (VITE_ENTRA_TENANT_SUBDOMAIN).
 * @returns {string}
 */
export function buildEntraAuthority(entraTenantId, mode = 'ciam', subdomain = '') {
    if (!entraTenantId) {
        console.warn('VITE_ENTRA_TENANT_ID is not set; MSAL authority may be invalid.');
    }
    if (mode === 'ciam') {
        const tenantGuid = isTenantGuid(entraTenantId) ? entraTenantId : '';
        const ciamSubdomain = subdomain || (isTenantGuid(entraTenantId) ? '' : entraTenantId);
        if (ciamSubdomain && tenantGuid) {
            return `https://${ciamSubdomain}.ciamlogin.com/${tenantGuid}`;
        }
        if (ciamSubdomain) {
            return `https://${ciamSubdomain}.ciamlogin.com/`;
        }
        if (tenantGuid) {
            return `https://${tenantGuid}.ciamlogin.com/${tenantGuid}`;
        }
    }
    return `https://login.microsoftonline.com/${entraTenantId}`;
}

const authority =
    authorityOverride || buildEntraAuthority(tenantId, authMode, tenantSubdomain);

export const msalConfig = {
    auth: {
        clientId,
        authority,
        redirectUri: readEnv('VITE_AUTH_REDIRECT_URI', 'http://localhost:32173/redirect'),
        postLogoutRedirectUri: '/',
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Scopes for sign-in / auth handoff. Graph scopes are not required.
 */
export const loginRequest = {
    scopes: entraLoginScopes,
};

/**
 * Password reset uses the same authority and scopes as sign-in (workforce or CIAM per VITE_ENTRA_AUTH_MODE).
 */
export const passwordResetRequest = {
    scopes: entraLoginScopes,
    authority,
};
